/**
 * 把正規化的 getMenuV2 / getItemDetailListV2 回應組成頁面使用的巢狀結構：餐點下直接掛
 * optionList / upgradeList / comboList，升級與套餐明細帶上該餐點的名稱、狀態與口味。
 *
 * 組裝一律「共用參照、不複製」：同一個口味、升級在整份結果只有一個物件，掛載它的餐點都指向它。
 * 複製會讓大型菜單（數百餐點 × 數個升級 × 數十明細 × 口味樹）在記憶體重新展開成數十 MB。
 * 因此組出來的口味、升級、套餐內容與明細都是**唯讀資料**（口味與升級跨餐點共用同一物件）：要寫入選取狀態必須先複製該餐點的子樹
 * （詳情頁的 initMenuItem* 即是）；只有餐點外殼（菜單頂層物件）可寫，供折扣、售況等欄位蓋章。
 * `freeze` 為 true 時深度凍結共用子樹，讓漏網的就地寫入當場拋錯。
 */

const COMBO_ITEM_TYPE_CODE = 2

/**
 * @param {object|null} data getMenuV2 的 data
 * @param {{ freeze?: boolean }} [options]
 * @returns {{ menuList: object[], remarkList: object[] }|null}
 */
export function assembleMenuV2(data, { freeze = false } = {}) {
	if (!data) return data
	const ctx = createContext(data, freeze, menuDetailFields)

	const menuList = (data.menuList || []).map(category => ({
		categoryId: category.categoryId,
		categoryName: category.categoryName,
		itemList: (category.itemIds || [])
			.map(itemId => ctx.itemMap[itemId])
			.filter(Boolean)
			.map(item => {
				const { itemStatus, ...shell } = item
				return assembleTopItem(ctx, shell, item.type === 'COMBO')
			}),
	}))

	return { menuList, remarkList: data.remarkList || [] }
}

/**
 * @param {object|null} data getItemDetailListV2 的 data
 * @param {{ freeze?: boolean }} [options]
 * @returns {object[]|null} 依 itemIds 順序（含重複、略過查無）的明細陣列
 */
export function assembleItemDetailListV2(data, { freeze = false } = {}) {
	if (!data) return data
	const ctx = createContext(data, freeze, itemDetailDetailFields)

	return (data.itemIds || [])
		.map(itemId => ctx.itemMap[itemId])
		.filter(Boolean)
		.map(item =>
			assembleTopItem(ctx, { ...item }, item.itemType === COMBO_ITEM_TYPE_CODE),
		)
}

function createContext(data, freeze, detailFields) {
	return {
		itemMap: data.itemMap || {},
		optionMap: data.optionMap || {},
		upgradeMap: data.upgradeMap || {},
		freeze,
		detailFields,
		optionListByItemId: new Map(),
		upgradeById: new Map(),
	}
}

function menuDetailFields(item) {
	return {
		categoryId: item.categoryId,
		itemName: item.itemName,
		itemStatus: item.itemStatus,
		availableForSale: item.availableForSale,
		availableQuantity: item.availableQuantity,
	}
}

function itemDetailDetailFields(item) {
	return {
		categoryId: item.categoryId,
		categoryName: item.categoryName,
		categoryStatus: item.categoryStatus,
		itemName: item.itemName,
		itemType: item.itemType,
		itemStatus: item.itemStatus,
		itemDisplayState: item.itemDisplayState,
		availableQuantity: item.availableQuantity,
		availableForSale: item.availableForSale,
	}
}

// 套餐作為頂層餐點時不呈現口味與升級；它的 optionIds 只在作為明細時使用
function assembleTopItem(ctx, shell, isCombo) {
	const { optionIds, upgradeIds, comboList, ...rest } = shell
	return {
		...rest,
		optionList: isCombo ? [] : optionListOf(ctx, shell.itemId),
		upgradeList: isCombo
			? []
			: (upgradeIds || []).map(id => upgradeOf(ctx, id)).filter(Boolean),
		comboList: isCombo ? (comboList || []).map(c => comboOf(ctx, c)) : [],
	}
}

function optionListOf(ctx, itemId) {
	let optionList = ctx.optionListByItemId.get(itemId)
	if (!optionList) {
		const item = ctx.itemMap[itemId]
		optionList = ((item && item.optionIds) || [])
			.map(id => ctx.optionMap[id])
			.filter(Boolean)
		share(ctx, optionList)
		ctx.optionListByItemId.set(itemId, optionList)
	}
	return optionList
}

function upgradeOf(ctx, upgradeId) {
	if (ctx.upgradeById.has(upgradeId)) return ctx.upgradeById.get(upgradeId)
	const upgrade = ctx.upgradeMap[upgradeId]
	const result = upgrade
		? {
				...upgrade,
				upgradeContentList: (upgrade.upgradeContentList || []).map(content => ({
					...content,
					upgradeContentDetailList: (content.upgradeContentDetailList || [])
						.map(detail => detailOf(ctx, detail))
						.filter(Boolean),
				})),
			}
		: null
	if (result) share(ctx, result)
	ctx.upgradeById.set(upgradeId, result)
	return result
}

function comboOf(ctx, content) {
	const result = {
		...content,
		detailList: (content.detailList || [])
			.map(detail => detailOf(ctx, detail))
			.filter(Boolean),
	}
	share(ctx, result)
	return result
}

// 明細餐點查無時略過該筆明細，與口味查無一致
function detailOf(ctx, detail) {
	const item = ctx.itemMap[detail.itemId]
	if (!item) return null
	return {
		...detail,
		...ctx.detailFields(item),
		itemId: detail.itemId,
		optionList: optionListOf(ctx, detail.itemId),
	}
}

function share(ctx, value) {
	if (ctx.freeze) deepFreeze(value)
}

export function deepFreeze(value) {
	if (value && typeof value === 'object' && !Object.isFrozen(value)) {
		Object.freeze(value)
		Object.values(value).forEach(deepFreeze)
	}
	return value
}
